import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { Job } from "@/data/types";
import { isUuid } from "@/lib/jobIdentifier";
import { isDispatchVisible } from "@/data/dispatchVisibility";

/** Keep compatibility with existing callers; filtering is now done client-side for schema safety. */
export const DB_BACKED_VIEWS = new Set(["all", "active", "completed", "qa", "materials", "dispatch"]);

// Only real DB columns — everything else lives in the metadata JSONB column.
// Mirrors the JobRow interface in job.service.ts.
type DbJobRow = {
  id: string;
  job_number: string | null;
  title: string | null;
  current_stage: string | null;
  permit_required: boolean | null;
  material_readiness: string | null;
  notes: string | null;
  address_line1: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  customers:
    | {
      first_name: string | null;
      last_name: string | null;
      address_line1?: string | null;
      address_line2?: string | null;
      city?: string | null;
      state?: string | null;
      postal_code?: string | null;
    }
    | Array<{
      first_name: string | null;
      last_name: string | null;
      address_line1?: string | null;
      address_line2?: string | null;
      city?: string | null;
      state?: string | null;
      postal_code?: string | null;
    }>
    | null;
  metadata: Record<string, unknown> | null;
  source_meta?: Job["sourceMeta"] | null;
  created_at: string;
  updated_at: string;
};

type JobMaterialRow = {
  job_id: string | null;
  sku: string | null;
  item_name: string | null;
  quantity_required: number | null;
  quantity_allocated: number | null;
  quantity_staged: number | null;
  quantity_installed: number | null;
  missing_items: number | null;
  field_purchase: boolean | null;
  inventory_assignment: string | null;
  lifecycle_stage: string | null;
  inventory_status: string | null;
  unit_cost_snapshot: number | null;
  unit_price_snapshot: number | null;
  prices_snapshot: number | null;
};

function deriveReadinessFromJobMaterials(
  current: Job["materialReadiness"],
  materials: Job["materialsList"],
): Job["materialReadiness"] {
  if (!materials.length) return current;
  const normalizedCurrent = String(current ?? "").trim();
  if (isTerminalMaterialReadiness(normalizedCurrent)) {
    return normalizedCurrent as Job["materialReadiness"];
  }

  const hasMissing = materials.some((m) => Math.max(0, Number(m.missingItems ?? 0)) > 0);
  if (hasMissing) return "Missing Items";

  const totalRequired = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityRequired ?? 0)), 0);
  const totalAllocated = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityAllocated ?? 0)), 0);
  const anyStagedOrInstalled = materials.some(
    (m) =>
      Math.max(0, Number(m.quantityStaged ?? 0)) > 0 ||
      Math.max(0, Number(m.quantityInstalled ?? 0)) > 0,
  );

  if (totalRequired > 0 && totalAllocated >= totalRequired) {
    return anyStagedOrInstalled ? "Staged for Job" : "Fully Received";
  }

  return current;
}

function isTerminalMaterialReadiness(value: unknown): boolean {
  return ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(value ?? "").trim());
}

function suppressStaleMissingMaterials(
  materials: Job["materialsList"],
  materialReadiness: Job["materialReadiness"],
): Job["materialsList"] {
  if (!isTerminalMaterialReadiness(materialReadiness)) return materials;
  return materials.map((material) => ({
    ...material,
    missingItems: 0,
    fieldPurchase: false,
  }));
}

function mapRow(row: DbJobRow): Job {
  const joinedCustomer =
    Array.isArray(row.customers)
      ? row.customers[0] ?? null
      : row.customers;
  const customerFromJoin = joinedCustomer
    ? [joinedCustomer.first_name, joinedCustomer.last_name].filter(Boolean).join(" ").trim()
    : "";
  const customerName =
    customerFromJoin ||
    "Unknown";

  const m = row.metadata ?? {};
  const sourceMeta =
    (row.source_meta as Job["sourceMeta"] | null | undefined) ??
    ((m.sourceMeta ?? m.source_meta) as Job["sourceMeta"] | undefined);

  return {
    id: row.id,
    jobNumber: row.job_number ?? row.id,
    customerName,
    address: {
      street:
        row.address_line1 ??
        [joinedCustomer?.address_line1, joinedCustomer?.address_line2].filter(Boolean).join(", ") ??
        "",
      city: row.city ?? joinedCustomer?.city ?? "",
      state: row.state ?? joinedCustomer?.state ?? "",
      zip: row.postal_code ?? joinedCustomer?.postal_code ?? "",
    },
    projectType: ((m.projectType as string | undefined) ?? "Other") as Job["projectType"],
    assignedCrew: (m.assignedCrew as string | undefined) ?? "",
    assignmentType: ((m.assignmentType as string | undefined) ?? "Unassigned") as Job["assignmentType"],
    scheduledInstallDate: (m.scheduledInstallDate as string | undefined) ?? "",
    permitRequired: row.permit_required ?? false,
    permitStatus: ((m.permitStatus as string | undefined) ?? "Not Required") as Job["permitStatus"],
    orderStatus: ((m.orderStatus as string | undefined) ?? "Not Ordered") as Job["orderStatus"],
    materialsStatus: ((m.materialsStatus as string | undefined) ?? "Not Ready") as Job["materialsStatus"],
    dispatchStatus: ((m.dispatchStatus as string | undefined) ?? "Not Ready") as Job["dispatchStatus"],
    contractorAssignmentStatus: (m.contractorAssignmentStatus as string | undefined) ?? "",
    currentStage: ((row.current_stage ?? m.currentStage as string | undefined) ?? "Sale Closed") as Job["currentStage"],
    notes: row.notes ?? "",
    description: (row.description as string | undefined) ?? "",
    issueFlag: (m.issueFlag as boolean | undefined) ?? false,
    scopeSummary: (m.scopeSummary as string | undefined) ?? "",
    payoutAmount: Number(m.payoutAmount ?? 0),
    assignedSubcontractor: (m.assignedSubcontractor as string | undefined) ?? "",
    targetInstallDate: (m.targetInstallDate as string | undefined) ?? "",
    activityHistory: (Array.isArray(m.activityHistory) ? (m.activityHistory as Job["activityHistory"]) : []) as Job["activityHistory"],
    photos: (Array.isArray(m.photos) ? (m.photos as string[]) : []) as string[],
    qaSubmitted: (m.qaSubmitted as boolean | undefined) ?? false,
    materialReadiness: (row.material_readiness ?? "Not Ordered") as Job["materialReadiness"],
    materialReadinessNotes: (m.materialReadinessNotes as string | undefined) ?? "",
    missingItemsList: (m.missingItemsList as string | undefined) ?? "",
    procurementStatus: ((m.procurementStatus as string | undefined) ?? "Not Ordered") as Job["procurementStatus"],
    warehouseStatus: ((m.warehouseStatus as string | undefined) ?? "Not Received") as Job["warehouseStatus"],
    finalDocumentationComplete: (m.finalDocumentationComplete as boolean | undefined) ?? false,
    customerSignoff: (m.customerSignoff as boolean | undefined) ?? false,
    closeoutNotes: (m.closeoutNotes as string | undefined) ?? "",
    closeoutSignoff: (m.closeoutSignoff as Job["closeoutSignoff"] | undefined) ?? undefined,
    closeoutStatus: ((m.closeoutStatus as string | undefined) ?? "Not Started") as Job["closeoutStatus"],
    branch: (m.branch as string | undefined) ?? "",
    region: (m.region as string | undefined) ?? "",
    salesRep: (m.salesRep as string | undefined) ?? "",
    phone: (m.phone as string | undefined) ?? "",
    email: (m.email as string | undefined) ?? "",
    secondaryContactName: (m.secondaryContactName as string | undefined) ?? "",
    secondaryContactPhone: (m.secondaryContactPhone as string | undefined) ?? "",
    secondaryContactEmail: (m.secondaryContactEmail as string | undefined) ?? "",
    timeSlot: (m.timeSlot as string | undefined) ?? undefined,
    scheduledShifts: (m.scheduledShifts as number | undefined) ?? undefined,
    permitDetail: (m.permitDetail as Job["permitDetail"] | undefined) ?? undefined,
    orderDetail: (m.orderDetail as Job["orderDetail"] | undefined) ?? undefined,
    materialsList: (m.materialsList as Job["materialsList"] | undefined) ?? undefined,
    financials: (m.financials as Job["financials"] | undefined) ?? undefined,
    marketing: (m.marketing as Job["marketing"] | undefined) ?? undefined,
    rescissionStatus: ((m.rescissionStatus as string | undefined) ?? "Cleared for Production") as Job["rescissionStatus"],
    projectConfig: (m.projectConfig as Job["projectConfig"] | undefined) ?? undefined,
    externalRef: (m.externalRef as Job["externalRef"] | undefined) ?? undefined,
    origin: (m.origin as Job["origin"] | undefined) ?? undefined,
    scheduledStartTime: (m.scheduledStartTime as string | undefined) ?? undefined,
    scheduledEndTime: (m.scheduledEndTime as string | undefined) ?? undefined,
    timezone: (m.timezone as string | undefined) ?? undefined,
    externalCalendarEventId: (m.externalCalendarEventId as string | undefined) ?? undefined,
    boundJobSpec: ((m.boundJobSpec ?? m.bound_job_spec) as Job["boundJobSpec"] | undefined) ?? undefined,
    executionReady: (m.executionReady as Job["executionReady"] | undefined) ?? undefined,
    contractDocument: (m.contractDocument as Job["contractDocument"] | undefined) ?? undefined,
    changeOrders: (m.changeOrders as Job["changeOrders"] | undefined) ?? undefined,
    scopeLocked: (m.scopeLocked as boolean | undefined) ?? undefined,
    opportunityId: (m.opportunityId as string | undefined) ?? undefined,
    appointmentResultId: (m.appointmentResultId as string | undefined) ?? undefined,
    intakeApproved: (m.intakeApproved as boolean | undefined) ?? undefined,
    approvedForOrdering: (m.approvedForOrdering as boolean | undefined) ?? undefined,
    holdStatus: (m.holdStatus as string | undefined) ?? undefined,
    holdReason: (m.holdReason as string | undefined) ?? undefined,
    planningStatus: (m.planningStatus as Job["planningStatus"] | undefined) ?? undefined,
    orderType: (m.orderType as Job["orderType"] | undefined) ?? undefined,
    selectedVendor: (m.selectedVendor as string | undefined) ?? undefined,
    selectedVendorType: (m.selectedVendorType as string | undefined) ?? undefined,
    schedulingStatus: (m.schedulingStatus as Job["schedulingStatus"] | undefined) ?? undefined,
    installReadiness: (m.installReadiness as Job["installReadiness"] | undefined) ?? undefined,
    jobMaterialStatus: (m.jobMaterialStatus as Job["jobMaterialStatus"] | undefined) ?? undefined,
    installStatus: (m.installStatus as Job["installStatus"] | undefined) ?? undefined,
    qaStatus: (m.qaStatus as Job["qaStatus"] | undefined) ?? undefined,
    reviewSent: (m.reviewSent as boolean | undefined) ?? undefined,
    reviewCompleted: (m.reviewCompleted as boolean | undefined) ?? undefined,
    reviewSatisfied: (m.reviewSatisfied as boolean | null | undefined) ?? null,
    reviewSentAt: (m.reviewSentAt as string | undefined) ?? undefined,
    referralRequested: (m.referralRequested as boolean | undefined) ?? undefined,
    referralReceived: (m.referralReceived as boolean | undefined) ?? undefined,
    referralRequestedAt: (m.referralRequestedAt as string | undefined) ?? undefined,
    googleReviewUrl: (m.googleReviewUrl as string | undefined) ?? undefined,
    siteReadiness: (m.siteReadiness as Job["siteReadiness"] | undefined) ?? undefined,
    buildReviewApproved: (m.buildReviewApproved as boolean | undefined) ?? undefined,
    buildReviewStatus: (m.buildReviewStatus as Job["buildReviewStatus"] | undefined) ?? undefined,
    buildReviewChecklist: (m.buildReviewChecklist as Job["buildReviewChecklist"] | undefined) ?? undefined,
    qaReviewerChecklist: (m.qaReviewerChecklist as Job["qaReviewerChecklist"] | undefined) ?? undefined,
    sourceMeta,
    tenantId: (m.tenantId as string | undefined) ?? undefined,
    createdAt: row.created_at ?? new Date().toISOString(),
    updatedAt: row.updated_at ?? new Date().toISOString(),
  } as Job;
}

function applyViewFilter(rows: Job[], view: string): Job[] {
  switch (view) {
    case "active":
      return rows.filter((row) => row.currentStage !== "Completed");
    case "completed":
      return rows.filter((row) => row.currentStage === "Completed");
    case "qa":
      return rows.filter((row) => row.currentStage === "QA Review");
    case "materials":
      return rows.filter((row) => row.materialReadiness === "Missing Items");
    case "dispatch":
      return rows.filter((row) => isDispatchVisible(row.currentStage, row.dispatchStatus));
    default:
      return rows;
  }
}

function applySearch(rows: Job[], search: string): Job[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => (
    row.jobNumber?.toLowerCase().includes(q) ||
    row.customerName?.toLowerCase().includes(q) ||
    row.projectType?.toLowerCase().includes(q) ||
    row.currentStage?.toLowerCase().includes(q)
  ));
}

function applyDbViewFilter(query: any, view: string) {
  switch (view) {
    case "active":
      return query.neq("current_stage", "Completed");
    case "completed":
      return query.eq("current_stage", "Completed");
    case "qa":
      return query.eq("current_stage", "QA Review");
    case "materials":
      return query.eq("material_readiness", "Missing Items");
    default:
      return query;
  }
}

function buildQuery(options: { count?: "exact"; view?: string } = {}) {
  const columns = [
    "id",
    "job_number",
    "title",
    "current_stage",
    "permit_required",
    "material_readiness",
    "notes",
    "address_line1",
    "city",
    "state",
    "postal_code",
    "metadata",
    "source_meta",
    "created_at",
    "updated_at",
    "customers(first_name,last_name,address_line1,address_line2,city,state,postal_code)",
  ].join(", ");

  const query = supabase
    .from("jobs")
    .select(columns, options.count ? { count: options.count } : undefined)
    .order("created_at", { ascending: false });

  return options.view ? applyDbViewFilter(query, options.view) : query;
}

function hydrateFromJobMaterials(rows: Job[], mats: JobMaterialRow[]): Job[] {
  const byJobId = new Map<string, Job["materialsList"]>();
  for (const mat of mats) {
    const jobId = String(mat.job_id ?? "").trim();
    const sku = String(mat.sku ?? "").trim();
    if (!jobId || !sku) continue;
    const line = {
      sku,
      itemName: String(mat.item_name ?? sku).trim() || sku,
      quantityRequired: Math.max(0, Number(mat.quantity_required ?? 0)),
      quantityAllocated: Math.max(0, Number(mat.quantity_allocated ?? 0)),
      quantityStaged: Math.max(0, Number(mat.quantity_staged ?? 0)),
      quantityInstalled: Math.max(0, Number(mat.quantity_installed ?? 0)),
      missingItems: Math.max(0, Number(mat.missing_items ?? 0)),
      unitCost: Number(mat.unit_cost_snapshot ?? mat.prices_snapshot ?? 0),
      unitPrice: Number(mat.unit_price_snapshot ?? mat.prices_snapshot ?? 0),
      fieldPurchase: Boolean(mat.field_purchase),
      inventoryAssignment: (mat.inventory_assignment === "Assigned" ? "Assigned" : "Stock") as Job["materialsList"][number]["inventoryAssignment"],
      lifecycleStage: (mat.lifecycle_stage ?? "Ordered") as Job["materialsList"][number]["lifecycleStage"],
      inventoryStatus: (mat.inventory_status ?? "In Stock") as Job["materialsList"][number]["inventoryStatus"],
    };
    const list = byJobId.get(jobId) ?? [];
    list.push(line);
    byJobId.set(jobId, list);
  }

  return rows.map((job) => {
    const canonical = byJobId.get(job.id);
    if (!canonical || canonical.length === 0) return job;
    const materialsList = suppressStaleMissingMaterials(canonical, job.materialReadiness);
    const missingItemsList = materialsList
      .filter((m) => m.missingItems > 0)
      .map((m) => m.itemName)
      .join(", ");
    const materialReadiness = deriveReadinessFromJobMaterials(job.materialReadiness, materialsList);
    return {
      ...job,
      materialsList,
      missingItemsList: missingItemsList || "",
      materialReadiness,
    };
  });
}

export function useJobsQuery({
  view,
  search,
  enabled = true,
  serverPagination = false,
  page = 0,
  pageSize = 50,
  hydrateMaterials = true,
  resolveSalesRepNames = true,
}: {
  view: string;
  search: string;
  enabled?: boolean;
  serverPagination?: boolean;
  page?: number;
  pageSize?: number;
  hydrateMaterials?: boolean;
  resolveSalesRepNames?: boolean;
}) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // Generation counter prevents stale load-more responses from landing after view/search reset
  const genRef = useRef(0);
  // Bump to force a re-fetch without changing view/search
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const gen = ++genRef.current;
    let cancelled = false;

    if (!enabled) {
      setLoading(false);
      setJobs([]);
      setTotalCount(0);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setJobs([]);
    setTotalCount(0);

    const safePageSize = Math.max(1, Number(pageSize) || 50);
    const safePage = Math.max(0, Number(page) || 0);
    const firstRow = safePage * safePageSize;
    const lastRow = firstRow + safePageSize - 1;

    (async () => {
      let orgId: string;
      try {
        orgId = await getCurrentOrganizationId();
      } catch {
        if (!cancelled) { setLoading(false); }
        return;
      }
      if (cancelled || genRef.current !== gen) return;

      const request = serverPagination
        ? buildQuery({ count: "exact", view }).eq("organization_id", orgId).range(firstRow, lastRow)
        : buildQuery().eq("organization_id", orgId);

      const { data, error, count } = await request;
      if (cancelled || genRef.current !== gen) return;
      if (error) {
        console.error("useJobsQuery error:", error);
      } else {
        console.log("useJobsQuery: rows returned =", data?.length ?? 0, data);
      }
      let rows = (data ?? []).map(mapRow);

      const jobIds = rows.map((r) => r.id).filter(Boolean);
      if (hydrateMaterials && jobIds.length > 0) {
        const { data: materialsRows, error: materialsError } = await supabase
          .from("job_materials")
          .select("job_id, sku, item_name, quantity_required, quantity_allocated, quantity_staged, quantity_installed, missing_items, field_purchase, inventory_assignment, lifecycle_stage, inventory_status, unit_cost_snapshot, unit_price_snapshot, prices_snapshot")
          .in("job_id", jobIds);
        if (cancelled || genRef.current !== gen) return;
        if (materialsError) {
          console.error("useJobsQuery [job_materials] error:", materialsError);
        } else {
          rows = hydrateFromJobMaterials(rows, (materialsRows ?? []) as JobMaterialRow[]);
        }
      }
      
      // Resolve salesRep UUIDs to names
      const salesRepUuids = rows
        .map(j => j.salesRep)
        .filter((s): s is string => !!s && isUuid(s));
      
      if (resolveSalesRepNames && salesRepUuids.length > 0) {
        const uniqueUuids = [...new Set(salesRepUuids)];
        const { data: repsData } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", uniqueUuids);
        if (cancelled || genRef.current !== gen) return;

        if (repsData) {
          const nameMap = new Map<string, string>();
          for (const rep of repsData) {
            const name = [rep.first_name, rep.last_name].filter(Boolean).join(" ");
            if (name) nameMap.set(rep.id, name);
          }
          rows = rows.map(j => 
            isUuid(j.salesRep) && nameMap.has(j.salesRep)
              ? { ...j, salesRep: nameMap.get(j.salesRep)! }
              : j
          );
        }
      }
      
      const filtered = serverPagination
        ? applySearch(rows, search)
        : applySearch(applyViewFilter(rows, view), search);
      if (cancelled || genRef.current !== gen) return;
      setJobs(filtered);
      setTotalCount(serverPagination ? count ?? filtered.length : filtered.length);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, hydrateMaterials, page, pageSize, resolveSalesRepNames, serverPagination, view, search, refreshKey]);

  function loadMore() {
    // no-op: fetching all rows in one query to keep compatibility across schema variants
  }

  return {
    jobs,
    setJobs,
    refresh,
    loading,
    loadingMore,
    hasMore: serverPagination ? (page + 1) * pageSize < totalCount : false,
    loadMore,
    totalCount,
  };
}
